import useCompletedChatMessage from 'hooks/chat/useCompletedChatMessage';
import useChatSummariesReconciliation from 'hooks/chat/useChatSummariesReconciliation';
import { useCallback, useEffect, useState } from 'react';
import chatService from 'services/chat';

const useAssignmentChatSummaries = ({ courseId, ownerAccountId }) => {
    const [chatsByAssignmentId, setChatsByAssignmentId] = useState(null);

    const refreshAssignmentChatSummary = useCallback(
        async ({ assignment_id: assignmentId }) => {
            if (!assignmentId || !ownerAccountId || !courseId) return;

            const nextChat = await chatService.getAssignmentChatSummary({
                active: true,
                owner_account_id: ownerAccountId,
                course_id: courseId,
                assignment_id: assignmentId,
            });

            if (!nextChat) return;

            setChatsByAssignmentId((current) => {
                if (!current || !current[assignmentId]) return current;

                return {
                    ...current,
                    [assignmentId]: nextChat,
                };
            });
        },
        [courseId, ownerAccountId]
    );

    useEffect(() => {
        if (!ownerAccountId || !courseId) {
            setChatsByAssignmentId(null);
            return;
        }

        let isMounted = true;

        const loadChatSummaries = async () => {
            try {
                const chats = await chatService.listChats(
                    {
                        active: true,
                        owner_account_id: ownerAccountId,
                        course_id: courseId,
                    },
                    { includeMessages: false }
                );

                if (!isMounted) return;

                setChatsByAssignmentId(
                    chats.reduce((result, chat) => {
                        result[chat.assignment_id] = chat;
                        return result;
                    }, {})
                );
            } catch (error) {
                if (!isMounted) return;
                console.error(error);
            }
        };

        loadChatSummaries();

        return () => {
            isMounted = false;
        };
    }, [courseId, ownerAccountId]);

    useCompletedChatMessage(refreshAssignmentChatSummary);
    useChatSummariesReconciliation({
        chats: chatsByAssignmentId ? Object.values(chatsByAssignmentId) : [],
        enabled: Boolean(chatsByAssignmentId),
        onOutOfSync: async (chatId) => {
            const currentChat = Object.values(chatsByAssignmentId || {}).find((chat) => chat?.id === chatId);
            if (!currentChat?.assignment_id) return;

            await refreshAssignmentChatSummary({
                assignment_id: currentChat.assignment_id,
            });
        },
    });

    return {
        chatsByAssignmentId,
        setChatsByAssignmentId,
    };
};

export default useAssignmentChatSummaries;
